// User Types - Matching the handler DTO structure
export interface OrgPathItem {
  id: string;
  name: string;
  level: string;
}

export interface User {
  id: number;
  external_id: string;
  user_uuid: string;
  account_uuid: string;
  email: string | null;
  first_name: string;
  last_name: string | null;
  employee_name: string | null;
  employee_id: string | null;
  phone: string | null;
  phone_extension: string | null;
  gender: string | null;
  profile_pic_url: string | null; // URL to profile photo
  employment_type: string | null;
  employment_status: string | null;
  job_title: string | null;
  job_title_id?: string | null;
  job_grade: string | null;
  job_code: string | null;
  eeoc_category: string | null;
  reporting_to_title: string | null;
  work_role: string | null;
  trainer_contact: boolean | null;
  position_description: string | null;
  standard_hours_per_week: string | null;
  date_of_birth: string | null;
  race: string | null;
  user_type: string | null;
  joining_date: string | null;
  hire_date: string | null;
  start_date: string | null;
  position_date: string | null;
  location_id: number | null;
  manager_id: number | null;
  manager?: string | null; // user_uuid of manager
  secondary_manager_id: number | null;
  secondary_manager?: string | null; // user_uuid of secondary manager
  hr_manager_id: number | null;
  hr_partner?: string | null; // user_uuid of HR partner
  entity_id: number | null;
  division_id: number | null;
  department_id: number | null;
  primary_org_unit_id?: string | null; // ID of the primary organizational unit
  org_path?: OrgPathItem[] | null; // Full organizational hierarchy path
  impersonate_account: boolean | null;
  is_hrmanager: boolean;
  base_pay: number | null;
  bonus_amount: number | null;
  bonus_percent: number | null;
  equity: number | null;
  pay_rate: number | null;

  // Lifecycle fields
  is_active: boolean;             // true = can use system
  deactivated_at?: string;        // ISO timestamp when deactivated
  termination_date?: string;      // ISO date of termination
  termination_reason?: string;    // Reason text
  is_anonymized: boolean;         // true = data scrubbed (GDPR)
  anonymized_at?: string;         // ISO timestamp when anonymized
  system_access_enabled?: boolean; // Keycloak access status
}

// Form data structure - matching employeeFormStore
export interface CreateUserRequest {
  first_name: string;
  last_name: string;
  email: string;
  employee_id?: string;
  phone?: string;
  gender?: string;
  profile_pic_url?: string | null; // URL to profile photo
  employment_type?: string;
  dob?: string;
  race?: string;
  employment_status?: string;
  job_title?: string;
  job_title_id?: string; // When provided, used to set user.job_title_id (string for precision)
  job_grade?: string;
  job_code?: string;
  eeoc_category?: string;
  reporting_to_title?: string;
  work_role?: string;
  trainer_contact?: boolean;
  hire_date?: string;
  start_date?: string;
  position_date?: string;
  location_id?: number;
  phone_extension?: string;
  standard_hr_per_week?: string; // API uses abbreviated "hr" not "hours"
  position_description?: string | null; // Attachment ID as string
  position_description_filename?: string | null; // Original filename for position description
  entity?: string;
  division?: string;
  branch?: string;
  primary_org_unit?: string; // Organizational unit ID
  manager?: string; // user_uuid as bigint string to avoid precision loss
  secondary_manager?: string; // user_uuid as bigint string to avoid precision loss
  hr_partner?: string; // user_uuid as bigint string to avoid precision loss
  is_hrmanager?: boolean;
  source?: 'Manual' | 'Import' | 'Integration'; // How the user was created
  education?: EducationRecord[];
  experience?: ExperienceRecord[];
  base_pay?: number | null;
  bonus_amount?: number | null;
  bonus_percent?: number | null;
  equity?: number | null;
  pay_rate?: number | null;
}

// Education Types - matching form structure
export interface UserEducation {
  id: number;
  user_id: number;
  school_name: string;
  level: string;
  field_of_study: string;
  year: number;
}

export interface EducationRecord {
  school_name: string;
  level: string;
  field_of_study: string;
  year: number;
}

export interface CreateEducationRequest {
  university: string;
  education: string;
  degree: string;
  year: number;
}

// Experience Types - matching form structure
export interface UserExperience {
  id: number;
  user_id: number;
  company_name: string | { String: string; Valid: boolean };
  position: string | { String: string; Valid: boolean };
  start_date: string | { Time: string; Valid: boolean };
  end_date?: string | { Time: string; Valid: boolean };
  description?: string | { String: string; Valid: boolean };
  total_years_experience?: number | null;
}

export interface ExperienceRecord {
  company: string;
  position: string;
  total_years_experience: number | null;
  start_date: string;
  end_date: string;
  description?: string;
}

export interface CreateExperienceRequest {
  company: string;
  position: string;
  total_years_experience: number | null;
  start_date: string;
  end_date: string;
  description?: string;
}

// API Response Types
export interface ApiResponse<T> {
  message?: string;
  data?: T;
  error?: string;
}

export interface UserResponse {
  user: User;
  education?: UserEducation[];
  experience?: UserExperience[];
}

export interface UserFullDetailRoleAssignment {
  assignment_id: string
  role_id: string
  role_name: string
  role_description: string | null
  role_type_id: number
  role_type_name: string
  role_type_display_name: string
  module_name: string
  module_display_name: string
  scope_instance_id: string | null
  scope_instance_name: string | null
  scope_id: number | null
  scope_name: string | null
  scope_position: number | null
  scope_is_system: boolean | null
  parent_instance_id: string | null
  include_descendants: boolean
  assignment_created_at: string
  assignment_source: 'manual' | 'auto_core_sync' | 'auto_new_employee' | 'auto_head_assignment'
}

// ── Explain Access types ────────────────────────────────────────────

export interface PermissionSource {
  role_id: string
  role_name: string
  role_type_display_name: string
  scope_instance_name: string | null
  assignment_source: string
}

export interface ExplainAccessPermission {
  module: string
  entity: string
  entity_name: string
  category: string
  action: string
  description?: string
  sources: PermissionSource[]
}

export interface ExplainAccessResponse {
  permissions: ExplainAccessPermission[]
  total_unique_permissions: number
  total_roles: number
}

export interface UserFullDetailUser {
  id: number
  user_uuid: string
  account_uuid: string
  fname: string
  lname: string | null
  email: string | null
  gender: string | null
  phone_number: string | null
  profile_pic_url: string | null
  employment_type: string | null
  employment_status: string | null
  job_title?: string | null
  job_title_id?: string | null
  job_grade: string | null
  job_code: string | null
  work_role: string | null
  employee_code: string | null
  joining_date: string | null
  hire_date: string | null
  is_superadmin: boolean
  is_hrmanager: boolean
  system_access_enabled: boolean
  manager_id: number | null
  manager_name?: string
  role_assignments: UserFullDetailRoleAssignment[]
}

export interface UserFullDetailResponse {
  success: boolean
  message: string
  user: UserFullDetailUser
  education?: UserEducation[]
  experience?: UserExperience[]
}

// ── Roles-detail endpoint (org/roles access panel) ──────────────────────────
// Lean response used by UserDetailDrawer.
// Contains only what the RBAC admin panel renders — no education/experience.

export interface UserRolesDetailUser {
  id: number
  user_uuid: string
  account_uuid: number
  fname: string
  lname: string | null
  email: string | null
  job_title: string | null
  work_role: string | null
  profile_pic_url: string | null
  is_active: boolean
  role_assignments: UserFullDetailRoleAssignment[]
}

export interface UserRolesDetailResponse {
  success: boolean
  message: string
  user: UserRolesDetailUser
}

export interface UsersListResponse {
  users: User[];
  total: number;
  account_id: number | string;
}

// PerformSpark Role assigned to a user
export interface UserPerformSparkRole {
  role_id: string;
  role_name: string;
  role_type_id: number;
  role_type_name: string;
  role_type_display_name: string;
}

// Org Assignment embedded in bulk response
export interface UserOrgAssignment {
  id: number;
  assignment_id: string;
  account_id: string;
  employee_id: string;
  scope_instance_id: string;
  assignment_type: 'PRIMARY' | 'SECONDARY' | 'MATRIX' | 'INTERIM';
  effective_from: string;
  effective_to: string | null;
  created_at: string;
  scope_name: string;
  level_name: string;
  level_position: number;
}

// User with org assignments and role (bulk endpoint response)
export interface UserWithOrgAssignments {
  id: number;
  user_uuid: string;
  account_uuid: string;
  external_id: string;
  email: string | null;
  first_name: string;
  last_name: string | null;
  employee_id: string | null;
  job_title: string | null;
  phone: string | null;
  profile_pic_url: string | null;
  employment_status: string | null;
  employment_type: string | null;
  is_hrmanager: boolean;
  system_access_enabled: boolean;
  org_assignments: UserOrgAssignment[];
  performspark_roles: UserPerformSparkRole[];
}

// Bulk users with org assignments response
export interface UsersWithOrgAssignmentsResponse {
  users: UserWithOrgAssignments[];
}

// Paginated users with org assignments response
export interface UsersWithOrgAssignmentsPaginatedResponse {
  users: UserWithOrgAssignments[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Request for paginated users with org assignments
export interface ListUsersWithOrgAssignmentsRequest {
  pageNumber?: number;
  pageCount?: number;
  sortParam?: string;
  sortOrder?: 'asc' | 'desc';
  roleId?: string;
  filters?: Array<{
    field: string;
    operator: string;
    value: string[];
  }>;
}

// Directory API: paginated list of all users (no scope/access filtering), with search
export interface DirectoryListResponse {
  users: User[];
  total: number;
  page: number;
  page_size: number;
}

export interface EducationResponse {
  message: string;
  education: UserEducation;
}

export interface EducationListResponse {
  education: UserEducation[];
  count: number;
}

export interface ExperienceResponse {
  message: string;
  experience: UserExperience;
}

export interface ExperienceListResponse {
  experience: UserExperience[];
  count: number;
}

// Employee interface for compatibility with existing UI
export interface Employee {
  id: string;
  user_uuid?: string; // Store as string to avoid JavaScript precision loss
  name: string;
  empId: string;
  email: string;
  phone?: string | null;
  jobTitle: string;
  grade: string;
  lastLogin: string;
  manager: string;
  hrPartner: string;
  location: string;
  department: string;
  unit?: string; // Organizational unit (e.g., 'Executive', 'Finance', 'Technology')
  source: string;
  status: 'Active' | 'Inactive' | 'Terminated' | 'Anonymized';
  avatarColor: string;
  avatarInitial: string;
  gender?: string | null;
  isHrManager?: boolean;
  profile_pic_url?: string | null; // URL to profile photo
  primaryOrgUnitId?: string; // ID of the primary organizational unit
  primaryOrgUnitName?: string; // Name of the primary organizational unit
  org_path?: OrgPathItem[] | null; // Full organizational hierarchy path (root → … → leaf)
}

// Utility function to convert API User to UI Employee
export const convertUserToEmployee = (
  user: User,
  locationMap?: Map<number, string>
): Employee => {
  const safeString = (value: unknown): string => {
    if (typeof value === 'string') {
      return value;
    }
    if (value === null || value === undefined) {
      return '';
    }
    return String(value);
  };

  const fullName = `${safeString(user.first_name).trim()} ${safeString(user.last_name).trim()}`.trim();
  const avatarInitial = safeString(user.first_name).charAt(0).toUpperCase() || 'U';
  const avatarColors = [
    'bg-[#b1e3ff]', 'bg-[#ffcb83]', 'bg-[#c6c7f8]', 'bg-[#a8c5da]',
    'bg-[#ffe999]', 'bg-[#a1e3cb]', 'bg-[#baedbd]'
  ];
  const randomColor = avatarColors[Math.floor(Math.random() * avatarColors.length)];
  const isHrManager = typeof user.is_hrmanager === 'boolean' ? user.is_hrmanager : Boolean(user.is_hrmanager);

  // Get location name from map or fallback to ID display
  let locationDisplay = 'N/A';
  if (user.location_id) {
    if (locationMap && locationMap.has(user.location_id)) {
      locationDisplay = locationMap.get(user.location_id)!;
    } else {
      locationDisplay = `Location #${user.location_id}`;
    }
  }

  return {
    id: user.user_uuid || user.id.toString(),
    user_uuid: user.user_uuid, // Already a string from User interface
    name: fullName,
    empId: safeString(user.employee_id) || safeString(user.external_id),
    email: safeString(user.email),
    phone: user.phone ?? null,
    jobTitle: safeString(user.job_title) || '-',
    grade: safeString(user.job_grade) || '-',
    lastLogin: new Date().toISOString(),
    manager: user.manager || (user.manager_id ? String(user.manager_id) : '-'),
    hrPartner: user.hr_partner || (user.hr_manager_id ? String(user.hr_manager_id) : '-'),
    location: locationDisplay,
    department: user.department_id ? `Department #${user.department_id}` : 'General',
    source: (user as any).source || (user.external_id ? 'Imported' : 'Manual'),
    // Map employment_status to status column
    // Use the same logic as getUserStatus in EmployeeList.tsx
    status: (() => {
      if (user.is_anonymized) return 'Anonymized' as const;

      // If user has system access, they should be considered Active regardless of employment_status
      // This handles cases where a terminated employee is re-activated
      if (user.is_active === true || user.system_access_enabled === true) {
        // But if employment_status is explicitly set to Inactive, respect that
        const employmentStatus = safeString(user.employment_status);
        if (employmentStatus === 'Inactive') return 'Inactive' as const;
        return 'Active' as const;
      }

      // If no system access, check employment_status
      const employmentStatus = safeString(user.employment_status);
      if (employmentStatus === 'Terminated') return 'Terminated' as const;
      if (employmentStatus === 'Inactive') return 'Inactive' as const;
      if (employmentStatus === 'Active') return 'Active' as const;

      // Fallback: if is_active is false and no employment_status
      if (user.is_active === false) return 'Inactive' as const;

      // Default to Active
      return 'Active' as const;
    })(),
    avatarColor: randomColor,
    avatarInitial: avatarInitial,
    gender: user.gender,
    isHrManager: isHrManager,
    profile_pic_url: user.profile_pic_url || null,
    primaryOrgUnitId: user.primary_org_unit_id ? String(user.primary_org_unit_id) : undefined,
    primaryOrgUnitName: (user as any).primary_org_unit_name || undefined,
    org_path: Array.isArray(user.org_path) ? user.org_path : undefined,
  };
};

/** Format unit as hierarchy path (e.g. "root > sdf > sdfsd") when org_path is present; otherwise fallback to primary unit name or "-". */
export function formatUnitHierarchy(employee: Employee): string {
  if (employee.org_path && employee.org_path.length > 0) {
    return employee.org_path.map((p) => p.name).join(' > ');
  }
  return employee.primaryOrgUnitName ?? employee.unit ?? '-';
}

// Helper function to safely convert dates
// Extract YYYY-MM-DD from a Date using local timezone (avoids UTC shift from toISOString)
const toLocalDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const safeFormatDate = (dateValue: any, fieldName: string): string | undefined => {
  console.log(`📅 Date formatting for ${fieldName}:`, {
    value: dateValue,
    type: typeof dateValue,
    isDate: dateValue instanceof Date,
    isString: typeof dateValue === 'string',
    isNull: dateValue === null,
    isUndefined: dateValue === undefined
  });

  if (!dateValue) return undefined;

  if (dateValue instanceof Date) {
    return toLocalDateString(dateValue);
  }

  if (typeof dateValue === 'string') {
    // If it's already in YYYY-MM-DD format, return as is
    if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateValue;
    }

    // Try to parse the string as a date
    const parsed = new Date(dateValue);
    if (!isNaN(parsed.getTime())) {
      return toLocalDateString(parsed);
    }
  }

  console.warn(`⚠️ Could not format date for ${fieldName}:`, dateValue);
  return undefined;
};

// Utility function to convert form data to API request
export const convertFormDataToCreateRequest = (formData: any): CreateUserRequest => {
  return {
    first_name: formData.firstName,
    last_name: formData.lastName,
    email: formData.email,
    employee_id: formData.employeeId,
    phone: formData.phone,
    gender: formData.gender || undefined,
    employment_type: formData.employmentType,
    dob: safeFormatDate(formData.dob, 'dob'),
    race: formData.race,
    employment_status: formData.employmentStatus,
    job_title: formData.title,
    job_title_id: formData.jobTitleId || undefined,
    job_grade: formData.jobGrade,
    job_code: formData.jobCode,
    eeoc_category: formData.eeocCategory,
    reporting_to_title: formData.reportingToTitle,
    work_role: formData.workRoles || undefined, // Send as string (work role name)
    trainer_contact: formData.traineryContact === 'Yes',
    hire_date: safeFormatDate(formData.hireDate, 'hireDate'),
    start_date: safeFormatDate(formData.startDate, 'startDate'),
    position_date: safeFormatDate(formData.positionDate, 'positionDate'),
    location_id: formData.location ? parseInt(formData.location, 10) : undefined,
    phone_extension: formData.phoneExt,
    standard_hr_per_week: formData.standardHrPerWeek, // API uses abbreviated "hr"
    position_description: formData.positionDescriptionAttachmentId || null, // Send attachment ID as string
    entity: formData.entity,
    division: formData.division,
    branch: formData.branch,
    primary_org_unit: formData.primaryOrgUnit,
    manager: formData.manager,
    secondary_manager: formData.secondaryManager,
    hr_partner: formData.hrPartner,
    is_hrmanager: formData.isHrManager,
    source: 'Manual', // Mark as manually created via sidebar
    profile_pic_url: formData.profilePicUrl || null,
    base_pay: formData.basePay,
    bonus_amount: formData.bonusAmount,
    bonus_percent: formData.bonusPercent,
    equity: formData.equity,
    pay_rate: formData.payRate,
    education: formData.educations?.map((edu: any) => ({
      school_name: edu.university,
      level: edu.education,
      field_of_study: edu.degree,
      year: parseInt(edu.year)
    })),
    experience: formData.experiences?.map((exp: any) => {
      // Handle both Date objects and string dates
      const formatDate = (date: any): string => {
        if (!date) return '';
        if (date instanceof Date) {
          return toLocalDateString(date);
        }
        if (typeof date === 'string') {
          // If it's already in YYYY-MM-DD format, return as is
          if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
            return date;
          }
          // If it's an ISO string, extract the date part
          if (date.includes('T')) {
            return date.split('T')[0];
          }
          // Try to parse and format
          const parsed = new Date(date);
          return isNaN(parsed.getTime()) ? '' : toLocalDateString(parsed);
        }
        return '';
      };

      const startDateFormatted = formatDate(exp.startDate);
      const endDateFormatted = formatDate(exp.endDate);

      console.log('📅 Experience date formatting:', {
        originalStartDate: exp.startDate,
        formattedStartDate: startDateFormatted,
        originalEndDate: exp.endDate,
        formattedEndDate: endDateFormatted
      });

      return {
        company: exp.company,
        position: exp.position,
        total_years_experience: (formData.totalYearsOfExperience === null || formData.totalYearsOfExperience === undefined)
          ? null
          : Number(formData.totalYearsOfExperience),
        start_date: startDateFormatted,
        end_date: endDateFormatted,
        description: exp.description
      };
    })
  };
};
